import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { useState, useEffect } from "react";
import heic2any from "heic2any";
import ArticleItemCustomerImageViewer from "./article-item-customer-image-viewer";
import ArticleItemReturnReason from "./article-item-return-reason";

type ArticleItemReasonProps = {
  lineItem: ReturnRequestLineItemDto;
  hideComment?: boolean;
  hideImages?: boolean;
  hideReason?: boolean;
};

export function ArticleItemReason({
  lineItem,
  hideComment,
  hideImages,
  hideReason,
}: ArticleItemReasonProps) {
  const [openedCustomerImageIndex, setOpenedCustomerImageIndex] =
    useState<number>(-1);
  const [convertedImages, setConvertedImages] = useState<
    Array<{ imageUrl: string; imageId: string }>
  >([]);
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    const convertHeicImages = async () => {
      if (!lineItem.customerImages?.length) return;

      const hasHeicImages = lineItem.customerImages.some(image => 
        image.imageUrl.toLowerCase().includes(".heic")
      );
      
      if (hasHeicImages) {
        setIsConverting(true);
      }

      const converted = await Promise.all(
        lineItem.customerImages.map(async (image) => {
          if (image.imageUrl.toLowerCase().includes(".heic")) {
            try {
              const response = await fetch(image.imageUrl);
              const blob = await response.blob();
              const convertedBlob = (await heic2any({
                blob,
                toType: "image/jpeg",
                quality: 0.8,
              })) as Blob;
              const convertedUrl = URL.createObjectURL(convertedBlob);
              return { imageUrl: convertedUrl, imageId: image.imageId };
            } catch (error) {
              console.error("Error converting HEIC image:", error);
              return image;
            }
          }
          return image;
        }),
      );
      setConvertedImages(converted);
      setIsConverting(false);
    };

    convertHeicImages();
  }, [lineItem.customerImages]);

  const imagesToDisplay =
    convertedImages.length > 0
      ? convertedImages
      : lineItem.customerImages || [];

  if (
    !lineItem.customerImages?.length &&
    !lineItem.comment &&
    !lineItem.reasonId
  ) {
    return null;
  }

  return (
    <div className="py-1 my-2 inline-flex flex-col gap-1">
      {!hideReason && (
        <span className="text-sm font-medium text-foreground">
          <ArticleItemReturnReason returnReasonId={lineItem.reasonId} />
        </span>
      )}
      {!hideComment && lineItem.comment && (
        <p className="text-sm text-muted-foreground">{lineItem.comment}</p>
      )}
      {!hideImages && lineItem.customerImages && lineItem.customerImages.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {isConverting ? (
            lineItem.customerImages.map((_, index) => (
              <div
                key={`loader-${index}`}
                className="size-10 border rounded-lg bg-muted animate-pulse flex items-center justify-center"
              >
                <div className="size-4 bg-muted-foreground/20 rounded-full animate-spin border-2 border-transparent border-t-muted-foreground/40" />
              </div>
            ))
          ) : (
            imagesToDisplay.map((image, index) => (
              <img
                key={image.imageUrl}
                role="button"
                className="size-10 border rounded-lg object-cover cursor-pointer"
                onClick={() => setOpenedCustomerImageIndex(index)}
                src={image.imageUrl}
              />
            ))
          )}
        </div>
      )}
      <ArticleItemCustomerImageViewer
        images={imagesToDisplay}
        currentIndex={openedCustomerImageIndex}
        setCurrentIndex={setOpenedCustomerImageIndex}
      />
    </div>
  );
}
